import type { FaceDesign } from '@v2/contracts'

const WIDTH = 512
const HEIGHT = 768
const PAPER = '#f3edda'
const INK = '#17251c'
const GOLD = '#a48a4f'

/** A content key: identical public faces can share a single GPU texture. */
export function faceKey(face: FaceDesign): string {
  return JSON.stringify([
    face.title, face.subtitle ?? '', face.body ?? '', face.footer ?? '',
    face.value ?? null, face.color, face.background ?? PAPER,
    face.style ?? 'botanical', face.symbol ?? 'leaf', face.image ?? '', face.imageRotation ?? 0,
  ])
}

/** Printed component artwork only. This function has no game or interaction state. */
export function createFaceCanvas(face: FaceDesign): HTMLCanvasElement {
  const canvas = document.createElement('canvas')
  canvas.width = WIDTH
  canvas.height = HEIGHT
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas2D is required to print component faces.')

  const random = seededRandom(faceKey(face))
  const color = face.color
  const background = face.background ?? PAPER
  ctx.fillStyle = background
  ctx.fillRect(0, 0, WIDTH, HEIGHT)

  // Fine, deterministic paper fibers survive close inspection without a bitmap asset.
  ctx.globalAlpha = 0.025
  for (let i = 0; i < 3800; i++) {
    ctx.fillStyle = i % 3 === 0 ? '#ffffff' : '#493c26'
    const x = random() * WIDTH
    const y = random() * HEIGHT
    ctx.fillRect(x, y, 0.4 + random() * 1.3, 0.4 + random() * 2.6)
  }
  ctx.globalAlpha = 1

  const style = face.style ?? 'botanical'
  if (style === 'ornament') {
    drawBack(ctx, face, random)
    return canvas
  }

  drawFrame(ctx, color)
  ctx.textAlign = 'center'
  ctx.fillStyle = readableInk(background, color)
  ctx.font = '600 18px Arial, sans-serif'
  drawSpacedText(ctx, (face.subtitle ?? '').toUpperCase(), WIDTH / 2, 66, 1.9, 388)

  ctx.fillStyle = readableInk(background)
  fittedText(ctx, face.title, WIDTH / 2, 115, 414, 46, 'Georgia, serif', 700)
  line(ctx, 202, 136, 310, 136, GOLD, 1)
  diamond(ctx, 256, 136, 3.5, GOLD)

  // Every illustration shares an arched aperture and a consistent print vocabulary.
  ctx.save()
  arch(ctx, 55, 165, 402, 367)
  ctx.clip()
  const wash = ctx.createLinearGradient(0, 165, 0, 532)
  wash.addColorStop(0, '#e7dfc7')
  wash.addColorStop(1, background)
  ctx.fillStyle = wash
  ctx.fillRect(55, 165, 402, 367)
  if (style === 'botanical') drawBotanical(ctx, color, random)
  else if (style === 'water') drawWater(ctx, color)
  else if (style === 'landscape') drawLandscape(ctx, color, random)
  else drawPlain(ctx, face)
  ctx.restore()

  arch(ctx, 55, 165, 402, 367)
  ctx.strokeStyle = GOLD
  ctx.lineWidth = 1.2
  ctx.stroke()

  // The value is a component label, never a score inferred by the renderer.
  const hasValue = face.value !== undefined
  if (hasValue) {
    ctx.fillStyle = background
    circle(ctx, 256, 539, 36)
    ctx.fill()
    ctx.strokeStyle = GOLD
    ctx.lineWidth = 1.4
    circle(ctx, 256, 539, 32)
    ctx.stroke()
    ctx.fillStyle = readableInk(background)
    fittedText(ctx, String(face.value), 256, 554, 56, 42, 'Georgia, serif', 700)
  }

  ctx.fillStyle = readableInk(background)
  ctx.font = '500 25px Arial, sans-serif'
  wrappedText(ctx, face.body ?? '', WIDTH / 2, hasValue ? 605 : 588, 390, 29, 3)

  line(ctx, 74, 691, 438, 691, GOLD, 0.8)
  drawSymbol(ctx, face.symbol ?? 'leaf', 82, 721, 12, color)
  ctx.fillStyle = readableInk(background, color)
  ctx.font = '600 17px Arial, sans-serif'
  drawSpacedText(ctx, (face.footer ?? '').toUpperCase(), 270, 726, 1.35, 320)
  return canvas
}

/** Aspect-aware printing for workspaces, zone plaques, and resource counters. */
export function createLabelCanvas(face: FaceDesign, aspect = 3): HTMLCanvasElement {
  const canvas = document.createElement('canvas')
  const safeAspect = Number.isFinite(aspect) && aspect > 0 ? Math.max(0.5, Math.min(aspect, 32)) : 3
  // Thin zone plaques need their own type scale. Preserve the physical aspect
  // instead of squashing a small workspace header across a long strip.
  canvas.width = safeAspect > 6 ? 2048 : 1024
  canvas.height = Math.round(canvas.width / safeAspect)
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas2D is required to print component labels.')
  const width = canvas.width
  const height = canvas.height
  const size = Math.min(width, height)
  const margin = Math.max(16, size * 0.065)
  const background = face.background ?? PAPER
  const ink = readableInk(background, face.color)
  ctx.fillStyle = background
  ctx.fillRect(0, 0, width, height)
  ctx.strokeStyle = face.color
  ctx.globalAlpha = 0.36
  ctx.lineWidth = Math.max(1, size / 400)
  roundedRect(ctx, margin, margin, width - 2 * margin, height - 2 * margin, size * 0.035)
  ctx.stroke()
  ctx.globalAlpha = 1
  ctx.fillStyle = ink

  if (safeAspect < 1.6) {
    ctx.textAlign = 'center'
    if (face.value !== undefined) {
      drawSymbol(ctx, face.symbol ?? 'leaf', width / 2, height * 0.24, size * 0.075, ink)
      fittedText(ctx, String(face.value), width / 2, height * 0.66, width * 0.64, size * 0.34, 'Georgia, serif', 700)
      fittedText(ctx, face.title, width / 2, height * 0.82, width * 0.73, size * 0.1, 'Arial, sans-serif', 700)
    } else {
      drawSymbol(ctx, face.symbol ?? 'leaf', width / 2, height * 0.35, size * 0.12, ink)
      fittedText(ctx, face.title, width / 2, height * 0.65, width * 0.75, size * 0.13, 'Georgia, serif', 700)
    }
  } else {
    const supportingText = face.subtitle ?? face.body ?? ''
    // The slab's bevel consumes the outer 4% of its UVs; keep print on the cap.
    const inset = Math.max(margin * 1.8, width * 0.06)
    const symbolX = width - inset - size * 0.12
    const textWidth = symbolX - inset - size * 0.3
    ctx.textAlign = 'left'
    fittedText(ctx, face.title, inset, height * (supportingText ? 0.45 : 0.64), textWidth, size * (supportingText ? 0.23 : 0.39), 'Georgia, serif', 700)
    if (supportingText) {
      ctx.font = `500 ${Math.max(14, size * 0.13)}px Arial, sans-serif`
      ctx.fillText(supportingText, inset, height * 0.72, textWidth)
    }
    drawSymbol(ctx, face.symbol ?? 'leaf', symbolX, height * 0.5, size * 0.12, ink)
  }
  return canvas
}

function drawFrame(ctx: CanvasRenderingContext2D, color: string): void {
  ctx.strokeStyle = GOLD
  ctx.lineWidth = 1.1
  roundedRect(ctx, 19, 19, WIDTH - 38, HEIGHT - 38, 15)
  ctx.stroke()
  ctx.globalAlpha = 0.4
  ctx.strokeStyle = color
  roundedRect(ctx, 25, 25, WIDTH - 50, HEIGHT - 50, 11)
  ctx.stroke()
  ctx.globalAlpha = 1
  for (const [x, y] of [[42, 43], [470, 43], [42, 725], [470, 725]]) {
    diamond(ctx, x!, y!, 3.5, GOLD)
  }
}

function drawBotanical(ctx: CanvasRenderingContext2D, color: string, random: () => number): void {
  ctx.fillStyle = '#e5c68b'
  ctx.globalAlpha = 0.45
  circle(ctx, 330, 269, 67)
  ctx.fill()
  ctx.globalAlpha = 1

  // Offset stems and tapered leaves give the print an organic silhouette.
  const stems = [
    { x: 252, y: 530, tipX: 248, tipY: 200, count: 7, scale: 1 },
    { x: 259, y: 530, tipX: 138, tipY: 287, count: 5, scale: 0.76 },
    { x: 253, y: 531, tipX: 380, tipY: 315, count: 5, scale: 0.69 },
  ]
  for (const stem of stems) {
    ctx.beginPath()
    ctx.moveTo(stem.x, stem.y)
    ctx.quadraticCurveTo(stem.tipX - 15, (stem.y + stem.tipY) / 2, stem.tipX, stem.tipY)
    ctx.strokeStyle = color
    ctx.lineWidth = 3 * stem.scale
    ctx.stroke()
    for (let i = 0; i < stem.count; i++) {
      const t = (i + 1) / (stem.count + 1)
      const x = stem.x + (stem.tipX - stem.x) * t - Math.sin(t * Math.PI) * 7
      const y = stem.y + (stem.tipY - stem.y) * t
      const size = (53 - t * 24) * stem.scale
      for (const side of [-1, 1]) {
        const angle = side < 0 ? -2.52 + t * 0.35 : -0.66 - t * 0.25
        drawLeaf(ctx, x, y - (side > 0 ? 9 : 0), size, angle, color, 0.68 + random() * 0.3)
      }
    }
    drawLeaf(ctx, stem.tipX, stem.tipY + 15, 31 * stem.scale, -Math.PI / 2, color, 0.9)
  }

  ctx.globalAlpha = 0.3
  for (let i = 0; i < 8; i++) {
    ctx.strokeStyle = color
    ctx.lineWidth = 0.7
    ctx.beginPath()
    ctx.ellipse(251, 527 + i * 2, 69 + i * 12, 5 + i, 0, 0, Math.PI * 2)
    ctx.stroke()
  }
  ctx.globalAlpha = 1
}

function drawLeaf(ctx: CanvasRenderingContext2D, x: number, y: number, length: number, angle: number, color: string, opacity = 1): void {
  const inheritedAlpha = ctx.globalAlpha
  ctx.save()
  ctx.translate(x, y)
  ctx.rotate(angle)
  ctx.globalAlpha = inheritedAlpha * opacity
  ctx.fillStyle = color
  ctx.beginPath()
  ctx.moveTo(0, 0)
  ctx.bezierCurveTo(length * 0.35, -length * 0.31, length * 0.75, -length * 0.23, length, 0)
  ctx.bezierCurveTo(length * 0.73, length * 0.25, length * 0.29, length * 0.33, 0, 0)
  ctx.fill()
  ctx.globalAlpha = inheritedAlpha * opacity * 0.42
  line(ctx, length * 0.08, 0, length * 0.91, 0, PAPER, 0.9)
  for (let i = 1; i < 4; i++) {
    const p = length * i / 5
    line(ctx, p, 0, p + length * 0.15, -length * 0.12, PAPER, 0.55)
    line(ctx, p, 0, p + length * 0.15, length * 0.12, PAPER, 0.55)
  }
  ctx.restore()
}

function drawWater(ctx: CanvasRenderingContext2D, color: string): void {
  ctx.fillStyle = '#dab679'
  circle(ctx, 256, 272, 52)
  ctx.fill()
  ctx.strokeStyle = '#b99c69'
  ctx.lineWidth = 0.8
  circle(ctx, 256, 272, 65)
  ctx.stroke()

  for (let i = 0; i < 17; i++) {
    const base = 328 + i * 14
    ctx.beginPath()
    ctx.moveTo(50, base)
    for (let x = 50; x <= 462; x += 4) {
      const y = base + Math.sin(x / 48 + i * 0.71) * (9 + i * 0.28)
      ctx.lineTo(x, y)
    }
    ctx.strokeStyle = color
    ctx.globalAlpha = 0.36 + i * 0.034
    ctx.lineWidth = i % 3 === 0 ? 4.4 : 1.6
    ctx.stroke()
  }
  ctx.globalAlpha = 1

  // Bright broken strokes read as a reflection on the hand-drawn water.
  for (let i = 0; i < 9; i++) {
    const y = 332 + i * 18
    const halfWidth = 12 + i * 4
    line(ctx, 256 - halfWidth, y, 256 + halfWidth, y - 2, '#f4e6bf', 3)
  }
  drawReed(ctx, 97, 534, -0.19, color)
  drawReed(ctx, 111, 538, 0.09, color)
  drawReed(ctx, 401, 539, 0.12, color)
}

function drawReed(ctx: CanvasRenderingContext2D, x: number, y: number, angle: number, color: string): void {
  ctx.save()
  ctx.translate(x, y)
  ctx.rotate(angle)
  line(ctx, 0, 0, 0, -109, color, 2)
  ctx.strokeStyle = color
  ctx.lineCap = 'round'
  ctx.lineWidth = 7
  ctx.beginPath()
  ctx.moveTo(0, -89)
  ctx.lineTo(0, -117)
  ctx.stroke()
  drawLeaf(ctx, 0, -42, 48, -0.75, color)
  ctx.restore()
}

function drawLandscape(ctx: CanvasRenderingContext2D, color: string, random: () => number): void {
  ctx.fillStyle = '#d9b273'
  circle(ctx, 336, 262, 44)
  ctx.fill()
  ctx.fillStyle = '#ccc5aa'
  polygon(ctx, [[35, 456], [136, 297], [212, 376], [285, 269], [480, 477]])
  ctx.fill()
  ctx.globalAlpha = 0.45
  ctx.fillStyle = color
  polygon(ctx, [[32, 478], [122, 373], [182, 412], [290, 314], [469, 479]])
  ctx.fill()
  ctx.globalAlpha = 0.8
  polygon(ctx, [[29, 533], [119, 443], [226, 473], [355, 371], [483, 505], [483, 540]])
  ctx.fill()
  ctx.globalAlpha = 1

  // Ivory scree and restrained hatch lines keep mountain silhouettes tactile.
  ctx.fillStyle = '#ece5d2'
  polygon(ctx, [[266, 291], [285, 269], [315, 304], [292, 291], [284, 307], [278, 292]])
  ctx.fill()
  polygon(ctx, [[114, 333], [136, 297], [163, 325], [141, 317], [132, 332], [128, 318]])
  ctx.fill()
  ctx.globalAlpha = 0.24
  for (let i = 0; i < 21; i++) {
    const x = 235 + random() * 84
    const y = 359 + random() * 50
    line(ctx, x, y, x - 20, y + 30, PAPER, 0.9)
  }
  ctx.globalAlpha = 1
  for (let i = 0; i < 10; i++) {
    const x = 61 + i * 43 + random() * 11
    const height = 29 + random() * 46
    drawPine(ctx, x, 539, height, color)
  }
  drawBird(ctx, 173, 246, 13, color)
  drawBird(ctx, 203, 262, 9, color)
}

function drawPine(ctx: CanvasRenderingContext2D, x: number, y: number, height: number, color: string): void {
  line(ctx, x, y, x, y - height, color, 2)
  ctx.fillStyle = color
  for (let i = 0; i < 4; i++) {
    const top = y - height + i * height * 0.17
    const halfWidth = height * (0.12 + i * 0.045)
    polygon(ctx, [[x, top], [x - halfWidth, top + height * 0.37], [x + halfWidth, top + height * 0.37]])
    ctx.fill()
  }
}

function drawBird(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, color: string): void {
  ctx.beginPath()
  ctx.moveTo(x - size, y)
  ctx.quadraticCurveTo(x - size / 2, y - size / 3, x, y + 2)
  ctx.quadraticCurveTo(x + size / 2, y - size / 3, x + size, y)
  ctx.strokeStyle = color
  ctx.lineWidth = 1.5
  ctx.stroke()
}

function drawPlain(ctx: CanvasRenderingContext2D, face: FaceDesign): void {
  ctx.strokeStyle = GOLD
  ctx.lineWidth = 1
  circle(ctx, 256, 348, 93)
  ctx.stroke()
  circle(ctx, 256, 348, 101)
  ctx.stroke()
  drawSymbol(ctx, face.symbol ?? 'leaf', 256, 348, 50, face.color)
}

function drawBack(ctx: CanvasRenderingContext2D, face: FaceDesign, random: () => number): void {
  const background = face.background ?? face.color
  ctx.fillStyle = background
  ctx.fillRect(0, 0, WIDTH, HEIGHT)
  const highlight = ctx.createRadialGradient(236, 290, 0, 256, 384, 440)
  highlight.addColorStop(0, '#ffffff14')
  highlight.addColorStop(1, '#00000025')
  ctx.fillStyle = highlight
  ctx.fillRect(0, 0, WIDTH, HEIGHT)
  ctx.strokeStyle = '#d2bd82'
  ctx.lineWidth = 1.5
  roundedRect(ctx, 23, 23, 466, 722, 16)
  ctx.stroke()
  ctx.lineWidth = 0.75
  roundedRect(ctx, 31, 31, 450, 706, 12)
  ctx.stroke()

  ctx.save()
  roundedRect(ctx, 43, 43, 426, 682, 8)
  ctx.clip()
  for (let row = 0; row < 11; row++) {
    for (let column = 0; column < 8; column++) {
      const x = 25 + column * 70 + (row % 2) * 35
      const y = 45 + row * 70
      ctx.globalAlpha = 0.15 + random() * 0.06
      drawSymbol(ctx, face.symbol ?? 'leaf', x, y, 13, '#dfcc99')
      diamond(ctx, x + 35, y + 35, 2, '#dfcc99')
    }
  }
  ctx.restore()

  ctx.fillStyle = background
  circle(ctx, 256, 365, 120)
  ctx.fill()
  for (const radius of [108, 114, 126]) {
    ctx.lineWidth = radius === 114 ? 2 : 0.7
    ctx.strokeStyle = '#d2bd82'
    circle(ctx, 256, 365, radius)
    ctx.stroke()
  }
  for (let i = 0; i < 32; i++) {
    const angle = i * Math.PI / 16
    const inner = i % 4 === 0 ? 129 : 133
    line(ctx, 256 + Math.cos(angle) * inner, 365 + Math.sin(angle) * inner,
      256 + Math.cos(angle) * 139, 365 + Math.sin(angle) * 139, '#d2bd82', 1)
  }
  drawSymbol(ctx, face.symbol ?? 'leaf', 256, 356, 46, '#e1ce98')
  ctx.textAlign = 'center'
  ctx.fillStyle = '#eee2bd'
  fittedText(ctx, face.title, 256, 564, 390, 40, 'Georgia, serif', 700)
  ctx.font = '600 17px Arial, sans-serif'
  drawSpacedText(ctx, (face.subtitle ?? '').toUpperCase(), 256, 599, 1.7, 370)
  diamond(ctx, 256, 645, 5, '#d2bd82')
}

function drawSymbol(ctx: CanvasRenderingContext2D, symbol: NonNullable<FaceDesign['symbol']>, x: number, y: number, size: number, color: string): void {
  ctx.save()
  ctx.strokeStyle = color
  ctx.fillStyle = color
  ctx.lineWidth = Math.max(1.3, size * 0.055)
  ctx.lineCap = 'round'
  if (symbol === 'leaf') {
    drawLeaf(ctx, x - size * 0.65, y + size * 0.66, size * 1.85, -Math.PI / 4, color)
    line(ctx, x - size * 0.82, y + size * 0.83, x + size * 0.42, y - size * 0.4, color, Math.max(1.2, size * 0.07))
  } else if (symbol === 'wave') {
    for (let row = -1; row <= 1; row++) {
      ctx.beginPath()
      ctx.moveTo(x - size, y + row * size * 0.48)
      ctx.bezierCurveTo(x - size * 0.45, y + row * size * 0.48 - size * 0.65,
        x + size * 0.45, y + row * size * 0.48 + size * 0.65, x + size, y + row * size * 0.48)
      ctx.stroke()
    }
  } else if (symbol === 'sun') {
    circle(ctx, x, y, size * 0.43)
    ctx.stroke()
    for (let i = 0; i < 12; i++) {
      const a = i * Math.PI / 6
      line(ctx, x + Math.cos(a) * size * 0.66, y + Math.sin(a) * size * 0.66,
        x + Math.cos(a) * size, y + Math.sin(a) * size, color, ctx.lineWidth)
    }
  } else {
    ctx.beginPath()
    for (let i = 0; i < 8; i++) {
      const angle = i * Math.PI / 4 - Math.PI / 2
      const radius = i % 2 === 0 ? size : size * 0.28
      const px = x + Math.cos(angle) * radius
      const py = y + Math.sin(angle) * radius
      if (i === 0) ctx.moveTo(px, py)
      else ctx.lineTo(px, py)
    }
    ctx.closePath()
    ctx.fill()
  }
  ctx.restore()
}

function seededRandom(key: string): () => number {
  let seed = 2166136261
  for (let i = 0; i < key.length; i++) seed = Math.imul(seed ^ key.charCodeAt(i), 16777619)
  return () => {
    seed = (seed + 0x6d2b79f5) | 0
    let value = Math.imul(seed ^ (seed >>> 15), 1 | seed)
    value ^= value + Math.imul(value ^ (value >>> 7), 61 | value)
    return ((value ^ (value >>> 14)) >>> 0) / 4294967296
  }
}

function fittedText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, width: number, initialSize: number, family: string, weight = 400): void {
  let size = initialSize
  ctx.font = `${weight} ${size}px ${family}`
  while (ctx.measureText(text).width > width && size > 15) {
    size -= 1
    ctx.font = `${weight} ${size}px ${family}`
  }
  ctx.fillText(text, x, y, width)
}

/** Keep small print legible when a game supplies a pale accent or a dark mat. */
function readableInk(background: string, preferred = INK): string {
  const luminance = (hex: string): number => {
    const raw = hex.replace(/^#/, '')
    const expanded = raw.length === 3 ? raw.split('').map(digit => digit + digit).join('') : raw
    if (!/^[\da-fA-F]{6}$/.test(expanded)) return 1
    const channels = [0, 2, 4].map(offset => {
      const value = parseInt(expanded.slice(offset, offset + 2), 16) / 255
      return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4
    })
    return channels[0]! * 0.2126 + channels[1]! * 0.7152 + channels[2]! * 0.0722
  }
  const bg = luminance(background)
  const contrast = (color: string): number => {
    const fg = luminance(color)
    return (Math.max(bg, fg) + 0.05) / (Math.min(bg, fg) + 0.05)
  }
  if (contrast(preferred) >= 4.5) return preferred
  return contrast(INK) >= contrast('#fff9e9') ? INK : '#fff9e9'
}

function wrappedText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, width: number, lineHeight: number, maxLines: number): void {
  const words = text.trim().split(/\s+/).filter(Boolean)
  const lines: string[] = []
  let current = ''
  for (const word of words) {
    const next = current ? `${current} ${word}` : word
    if (current && ctx.measureText(next).width > width) {
      lines.push(current)
      current = word
    } else current = next
  }
  if (current) lines.push(current)
  for (let i = 0; i < Math.min(lines.length, maxLines); i++) {
    let printed = lines[i]!
    if (i === maxLines - 1 && lines.length > maxLines) {
      while (ctx.measureText(`${printed}…`).width > width && printed.length > 0) printed = printed.slice(0, -1)
      printed += '…'
    }
    ctx.fillText(printed, x, y + i * lineHeight, width)
  }
}

function drawSpacedText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, spacing: number, maxWidth: number): void {
  const letters = Array.from(text)
  const textWidth = ctx.measureText(text).width + Math.max(0, letters.length - 1) * spacing
  if (textWidth > maxWidth) {
    ctx.fillText(text, x, y, maxWidth)
    return
  }
  const alignment = ctx.textAlign
  ctx.textAlign = 'left'
  let cursor = x - textWidth / 2
  for (const letter of letters) {
    ctx.fillText(letter, cursor, y)
    cursor += ctx.measureText(letter).width + spacing
  }
  ctx.textAlign = alignment
}

function line(ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number, color: string, width: number): void {
  ctx.beginPath()
  ctx.moveTo(x1, y1)
  ctx.lineTo(x2, y2)
  ctx.strokeStyle = color
  ctx.lineWidth = width
  ctx.stroke()
}

function circle(ctx: CanvasRenderingContext2D, x: number, y: number, radius: number): void {
  ctx.beginPath()
  ctx.arc(x, y, radius, 0, Math.PI * 2)
}

function diamond(ctx: CanvasRenderingContext2D, x: number, y: number, radius: number, color: string): void {
  polygon(ctx, [[x, y - radius], [x + radius, y], [x, y + radius], [x - radius, y]])
  ctx.fillStyle = color
  ctx.fill()
}

function polygon(ctx: CanvasRenderingContext2D, points: readonly (readonly [number, number])[]): void {
  ctx.beginPath()
  points.forEach(([x, y], index) => index === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y))
  ctx.closePath()
}

function roundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radius: number): void {
  ctx.beginPath()
  ctx.roundRect(x, y, width, height, radius)
}

function arch(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number): void {
  const radius = width / 2
  ctx.beginPath()
  ctx.moveTo(x, y + height)
  ctx.lineTo(x, y + radius)
  ctx.arc(x + radius, y + radius, radius, Math.PI, Math.PI * 2)
  ctx.lineTo(x + width, y + height)
  ctx.closePath()
}
